import { CameraAltOutlined, CenterFocusStrongOutlined } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { useEffect, useRef, useState } from "react";

const tInstruction =
  "Hold the back of the license about an arm's length from the camera, with the barcode well lit and filling the red scan line.";
const tLooking = "Looking for barcode…";
const tCancel = "Cancel";
const tRefocus = "Refocus";
const tUseScan = "Use this scan";
const tDecodedLabel = "Decoded barcode (debug)";
const tDecodedHelper = 'Verify the text looks correct, then click "Use this scan" to populate the form.';
const tPermissionDenied =
  "Camera access was denied. Please allow camera access or enter the visitor information manually.";
const tNoCamera = "We couldn't find a camera. Please enter the visitor information manually.";
const tGenericError = "Could not start the camera. Please enter the visitor information manually.";

/** focusMode is in the MediaCapture spec but not yet in TS lib.dom; allow it as an advanced constraint. */
type FocusConstraintSet = MediaTrackConstraintSet & { focusMode?: "manual" | "single-shot" | "continuous" | "none" };

/**
 * Minimal typing for the browser's native BarcodeDetector API (Chromium / Safari 17+).
 * Not yet in lib.dom, so we declare just what we use.
 */
interface NativeBarcodeDetectorInstance {
  detect(source: HTMLVideoElement | ImageBitmapSource): Promise<{ rawValue: string; format: string }[]>;
}
interface NativeBarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): NativeBarcodeDetectorInstance;
  getSupportedFormats(): Promise<string[]>;
}
function getNativeBarcodeDetector(): NativeBarcodeDetectorConstructor | undefined {
  return (window as unknown as { BarcodeDetector?: NativeBarcodeDetectorConstructor }).BarcodeDetector;
}

export interface VisitorIdScannerProps {
  onScan: (rawBarcodeText: string) => void;
  onCancel: () => void;
}

function buildReader(): BrowserMultiFormatReader {
  const hints = new Map();
  // Restricting to a small set of 2D formats keeps the decoder focused while still
  // letting us verify the scanner is alive by aiming at a phone QR code, etc.
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.PDF_417,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.AZTEC,
  ]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints);
}

function translateCameraError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      return tPermissionDenied;
    }
    if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
      return tNoCamera;
    }
  }
  return tGenericError;
}

/** Try to request continuous autofocus. Silently ignored by cameras that don't support it. */
async function applyContinuousFocus(track: MediaStreamTrack): Promise<void> {
  try {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" } as FocusConstraintSet] });
  } catch {
    // Camera doesn't support focusMode; not fatal.
  }
}

/** Toggle focus mode to coax cameras with autofocus into a fresh focus cycle. */
async function triggerRefocus(track: MediaStreamTrack): Promise<void> {
  try {
    await track.applyConstraints({ advanced: [{ focusMode: "manual" } as FocusConstraintSet] });
  } catch {
    // ignore
  }
  try {
    await track.applyConstraints({ advanced: [{ focusMode: "continuous" } as FocusConstraintSet] });
  } catch {
    // ignore
  }
}

/**
 * Runs a polling decode loop against the browser's native BarcodeDetector.
 * Returns a stop function. Caller is responsible for ensuring the video is
 * already playing.
 */
function startNativeDetectorLoop(
  Detector: NativeBarcodeDetectorConstructor,
  videoEl: HTMLVideoElement,
  onFrame: () => void,
  onResult: (text: string) => void,
): () => void {
  const detector = new Detector({ formats: ["pdf417", "qr_code", "data_matrix", "aztec"] });
  let stopped = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (stopped) {
      return;
    }
    onFrame();
    try {
      const codes = await detector.detect(videoEl);
      if (!stopped && codes.length > 0) {
        onResult(codes[0].rawValue);
      }
    } catch {
      // Native decoder can throw intermittently on a bad frame; just keep going.
    }
    if (!stopped) {
      timeoutId = setTimeout(() => void tick(), 200);
    }
  };

  void tick();

  return () => {
    stopped = true;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  };
}

export function VisitorIdScanner({ onScan, onCancel }: VisitorIdScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [decodedText, setDecodedText] = useState<string | null>(null);
  const [framesAnalyzed, setFramesAnalyzed] = useState(0);
  const [videoResolution, setVideoResolution] = useState<string | null>(null);
  const [activeDecoder, setActiveDecoder] = useState<string | null>(null);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    let stopLoop: (() => void) | null = null;
    let stream: MediaStream | null = null;
    let cancelled = false;

    const handleFrame = () => setFramesAnalyzed((n) => n + 1);
    const handleResult = (text: string) => setDecodedText((prev) => (prev === text ? prev : text));

    const startup = async () => {
      try {
        // Request as much resolution as the camera will give us — PDF417 bars are
        // very thin and need plenty of pixels to decode reliably.
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            height: { ideal: 1080 },
            width: { ideal: 1920 },
          },
        });

        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          trackRef.current = videoTrack;
          await applyContinuousFocus(videoTrack);
          const settings = videoTrack.getSettings();
          if (settings.width && settings.height) {
            setVideoResolution(`${settings.width}\u00d7${settings.height}`);
          }
        }

        if (cancelled) {
          return;
        }

        // Prefer the browser's native BarcodeDetector when it supports PDF417 —
        // on Android Chrome it uses ML Kit and on iOS 17+ Safari it uses Vision,
        // both of which are dramatically better at PDF417 than ZXing.
        const Detector = getNativeBarcodeDetector();
        let nativeStarted = false;
        if (Detector) {
          try {
            const formats = await Detector.getSupportedFormats();
            if (formats.includes("pdf417")) {
              stopLoop = startNativeDetectorLoop(Detector, videoRef.current, handleFrame, handleResult);
              setActiveDecoder("Native (BarcodeDetector)");
              nativeStarted = true;
            }
          } catch {
            // Fall through to ZXing.
          }
        }

        if (!nativeStarted) {
          const reader = buildReader();
          const controls: IScannerControls = await reader.decodeFromVideoElement(videoRef.current, (result) => {
            handleFrame();
            if (result) {
              handleResult(result.getText());
            }
          });
          stopLoop = () => controls.stop();
          setActiveDecoder("ZXing");
        }

        if (cancelled) {
          stopLoop?.();
          stopLoop = null;
          return;
        }

        setIsReady(true);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(translateCameraError(err));
        }
      }
    };

    void startup();

    return () => {
      cancelled = true;
      stopLoop?.();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      stream?.getTracks().forEach((track) => track.stop());
      trackRef.current = null;
    };
  }, []);

  const handleRefocus = () => {
    const track = trackRef.current;
    if (track) {
      void triggerRefocus(track);
    }
  };

  const handleUseScan = () => {
    if (decodedText) {
      onScan(decodedText);
    }
  };

  return (
    <Stack gap={2}>
      <Typography color="text.secondary" variant="body2">
        {tInstruction}
      </Typography>

      <Box
        sx={{
          alignItems: "center",
          bgcolor: "common.black",
          borderRadius: 1,
          display: "flex",
          justifyContent: "center",
          maxHeight: 360,
          minHeight: 240,
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        <video
          muted
          playsInline
          ref={videoRef}
          style={{ height: "auto", maxHeight: 360, maxWidth: "100%", objectFit: "contain", width: "100%" }}
        />

        {/* Red horizontal scan line */}
        {isReady ? (
          <Box
            sx={{
              bgcolor: "error.main",
              boxShadow: "0 0 8px rgba(255,0,0,0.85)",
              height: 3,
              left: "5%",
              opacity: 0.85,
              pointerEvents: "none",
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              width: "90%",
            }}
          />
        ) : null}

        {!isReady && !error ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{ color: "common.white", inset: 0, justifyContent: "center", position: "absolute" }}
          >
            <CircularProgress color="inherit" size={24} />
            <Typography variant="body2">{tLooking}</Typography>
          </Stack>
        ) : null}
      </Box>

      {error ? <Alert severity="warning">{error}</Alert> : null}

      <Stack direction="row" flexWrap="wrap" gap={2}>
        <Typography color="text.secondary" variant="caption">
          Frames analyzed: {framesAnalyzed}
        </Typography>
        {videoResolution ? (
          <Typography color="text.secondary" variant="caption">
            Resolution: {videoResolution}
          </Typography>
        ) : null}
        {activeDecoder ? (
          <Typography color="text.secondary" variant="caption">
            Decoder: {activeDecoder}
          </Typography>
        ) : null}
      </Stack>

      <TextField
        fullWidth
        helperText={tDecodedHelper}
        InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 12 } }}
        label={tDecodedLabel}
        maxRows={6}
        minRows={3}
        multiline
        value={decodedText ?? ""}
      />

      <Stack direction={{ sm: "row", xs: "column" }} gap={1} justifyContent="flex-end">
        <Button onClick={onCancel} variant="outlined">
          {tCancel}
        </Button>
        <Button
          disabled={!isReady}
          onClick={handleRefocus}
          startIcon={<CenterFocusStrongOutlined />}
          variant="outlined"
        >
          {tRefocus}
        </Button>
        <Button disabled={!decodedText} onClick={handleUseScan} startIcon={<CameraAltOutlined />} variant="contained">
          {tUseScan}
        </Button>
      </Stack>
    </Stack>
  );
}
